/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU64Decoder,
  getU64Encoder,
  getU8Decoder,
  getU8Encoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  getAddressFromResolvedInstructionAccount,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { INITIALIZER_PROGRAM_ADDRESS } from '../programs';

export const CURVE_SWAP_EXACT_IN_DISCRIMINATOR = new Uint8Array([
  196, 247, 195, 126, 227, 27, 166, 93,
]);

export function getCurveSwapExactInDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    CURVE_SWAP_EXACT_IN_DISCRIMINATOR,
  );
}

export type CurveSwapExactInInstruction<
  TProgram extends string = typeof INITIALIZER_PROGRAM_ADDRESS,
  TAccountLaunch extends string | AccountMeta<string> = string,
  TAccountLaunchAuthority extends string | AccountMeta<string> = string,
  TAccountBaseVault extends string | AccountMeta<string> = string,
  TAccountQuoteVault extends string | AccountMeta<string> = string,
  TAccountUserBaseAccount extends string | AccountMeta<string> = string,
  TAccountUserQuoteAccount extends string | AccountMeta<string> = string,
  TAccountBaseMint extends string | AccountMeta<string> = string,
  TAccountQuoteMint extends string | AccountMeta<string> = string,
  TAccountUser extends string | AccountMeta<string> = string,
  TAccountHookProgram extends string | AccountMeta<string> = string,
  TAccountLaunchFeeState extends string | AccountMeta<string> = string,
  TAccountBaseTokenProgram extends string | AccountMeta<string> = string,
  TAccountQuoteTokenProgram extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountLaunch extends string
        ? WritableAccount<TAccountLaunch>
        : TAccountLaunch,
      TAccountLaunchAuthority extends string
        ? ReadonlyAccount<TAccountLaunchAuthority>
        : TAccountLaunchAuthority,
      TAccountBaseVault extends string
        ? WritableAccount<TAccountBaseVault>
        : TAccountBaseVault,
      TAccountQuoteVault extends string
        ? WritableAccount<TAccountQuoteVault>
        : TAccountQuoteVault,
      TAccountUserBaseAccount extends string
        ? WritableAccount<TAccountUserBaseAccount>
        : TAccountUserBaseAccount,
      TAccountUserQuoteAccount extends string
        ? WritableAccount<TAccountUserQuoteAccount>
        : TAccountUserQuoteAccount,
      TAccountBaseMint extends string
        ? ReadonlyAccount<TAccountBaseMint>
        : TAccountBaseMint,
      TAccountQuoteMint extends string
        ? ReadonlyAccount<TAccountQuoteMint>
        : TAccountQuoteMint,
      TAccountUser extends string
        ? ReadonlySignerAccount<TAccountUser> & AccountSignerMeta<TAccountUser>
        : TAccountUser,
      TAccountHookProgram extends string
        ? ReadonlyAccount<TAccountHookProgram>
        : TAccountHookProgram,
      TAccountLaunchFeeState extends string
        ? WritableAccount<TAccountLaunchFeeState>
        : TAccountLaunchFeeState,
      TAccountBaseTokenProgram extends string
        ? ReadonlyAccount<TAccountBaseTokenProgram>
        : TAccountBaseTokenProgram,
      TAccountQuoteTokenProgram extends string
        ? ReadonlyAccount<TAccountQuoteTokenProgram>
        : TAccountQuoteTokenProgram,
      ...TRemainingAccounts,
    ]
  >;

export type CurveSwapExactInInstructionData = {
  discriminator: ReadonlyUint8Array;
  amountIn: bigint;
  minAmountOut: bigint;
  tradeDirection: number;
};

export type CurveSwapExactInInstructionDataArgs = {
  amountIn: number | bigint;
  minAmountOut: number | bigint;
  tradeDirection: number;
};

export function getCurveSwapExactInInstructionDataEncoder(): FixedSizeEncoder<CurveSwapExactInInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['amountIn', getU64Encoder()],
      ['minAmountOut', getU64Encoder()],
      ['tradeDirection', getU8Encoder()],
    ]),
    (value) => ({ ...value, discriminator: CURVE_SWAP_EXACT_IN_DISCRIMINATOR }),
  );
}

export function getCurveSwapExactInInstructionDataDecoder(): FixedSizeDecoder<CurveSwapExactInInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['amountIn', getU64Decoder()],
    ['minAmountOut', getU64Decoder()],
    ['tradeDirection', getU8Decoder()],
  ]);
}

export function getCurveSwapExactInInstructionDataCodec(): FixedSizeCodec<
  CurveSwapExactInInstructionDataArgs,
  CurveSwapExactInInstructionData
> {
  return combineCodec(
    getCurveSwapExactInInstructionDataEncoder(),
    getCurveSwapExactInInstructionDataDecoder(),
  );
}

export type CurveSwapExactInAsyncInput<
  TAccountLaunch extends string = string,
  TAccountLaunchAuthority extends string = string,
  TAccountBaseVault extends string = string,
  TAccountQuoteVault extends string = string,
  TAccountUserBaseAccount extends string = string,
  TAccountUserQuoteAccount extends string = string,
  TAccountBaseMint extends string = string,
  TAccountQuoteMint extends string = string,
  TAccountUser extends string = string,
  TAccountHookProgram extends string = string,
  TAccountLaunchFeeState extends string = string,
  TAccountBaseTokenProgram extends string = string,
  TAccountQuoteTokenProgram extends string = string,
> = {
  launch: Address<TAccountLaunch>;
  launchAuthority: Address<TAccountLaunchAuthority>;
  baseVault: Address<TAccountBaseVault>;
  quoteVault: Address<TAccountQuoteVault>;
  userBaseAccount: Address<TAccountUserBaseAccount>;
  userQuoteAccount: Address<TAccountUserQuoteAccount>;
  baseMint: Address<TAccountBaseMint>;
  quoteMint: Address<TAccountQuoteMint>;
  user: TransactionSigner<TAccountUser>;
  /** Optional hook program (must match launch.hook_program if set) */
  hookProgram?: Address<TAccountHookProgram>;
  launchFeeState?: Address<TAccountLaunchFeeState>;
  baseTokenProgram: Address<TAccountBaseTokenProgram>;
  quoteTokenProgram: Address<TAccountQuoteTokenProgram>;
  amountIn: CurveSwapExactInInstructionDataArgs['amountIn'];
  minAmountOut: CurveSwapExactInInstructionDataArgs['minAmountOut'];
  tradeDirection: CurveSwapExactInInstructionDataArgs['tradeDirection'];
};

export async function getCurveSwapExactInInstructionAsync<
  TAccountLaunch extends string,
  TAccountLaunchAuthority extends string,
  TAccountBaseVault extends string,
  TAccountQuoteVault extends string,
  TAccountUserBaseAccount extends string,
  TAccountUserQuoteAccount extends string,
  TAccountBaseMint extends string,
  TAccountQuoteMint extends string,
  TAccountUser extends string,
  TAccountHookProgram extends string,
  TAccountLaunchFeeState extends string,
  TAccountBaseTokenProgram extends string,
  TAccountQuoteTokenProgram extends string,
  TProgramAddress extends Address = typeof INITIALIZER_PROGRAM_ADDRESS,
>(
  input: CurveSwapExactInAsyncInput<
    TAccountLaunch,
    TAccountLaunchAuthority,
    TAccountBaseVault,
    TAccountQuoteVault,
    TAccountUserBaseAccount,
    TAccountUserQuoteAccount,
    TAccountBaseMint,
    TAccountQuoteMint,
    TAccountUser,
    TAccountHookProgram,
    TAccountLaunchFeeState,
    TAccountBaseTokenProgram,
    TAccountQuoteTokenProgram
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  CurveSwapExactInInstruction<
    TProgramAddress,
    TAccountLaunch,
    TAccountLaunchAuthority,
    TAccountBaseVault,
    TAccountQuoteVault,
    TAccountUserBaseAccount,
    TAccountUserQuoteAccount,
    TAccountBaseMint,
    TAccountQuoteMint,
    TAccountUser,
    TAccountHookProgram,
    TAccountLaunchFeeState,
    TAccountBaseTokenProgram,
    TAccountQuoteTokenProgram
  >
> {
  // Program address.
  const programAddress = config?.programAddress ?? INITIALIZER_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    launch: { value: input.launch ?? null, isWritable: true },
    launchAuthority: {
      value: input.launchAuthority ?? null,
      isWritable: false,
    },
    baseVault: { value: input.baseVault ?? null, isWritable: true },
    quoteVault: { value: input.quoteVault ?? null, isWritable: true },
    userBaseAccount: { value: input.userBaseAccount ?? null, isWritable: true },
    userQuoteAccount: {
      value: input.userQuoteAccount ?? null,
      isWritable: true,
    },
    baseMint: { value: input.baseMint ?? null, isWritable: false },
    quoteMint: { value: input.quoteMint ?? null, isWritable: false },
    user: { value: input.user ?? null, isWritable: false },
    hookProgram: { value: input.hookProgram ?? null, isWritable: false },
    launchFeeState: { value: input.launchFeeState ?? null, isWritable: true },
    baseTokenProgram: {
      value: input.baseTokenProgram ?? null,
      isWritable: false,
    },
    quoteTokenProgram: {
      value: input.quoteTokenProgram ?? null,
      isWritable: false,
    },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.launchFeeState.value) {
    accounts.launchFeeState.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            108, 97, 117, 110, 99, 104, 95, 102, 101, 101, 95, 115, 116, 97,
            116, 101,
          ]),
        ),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount(
            'launch',
            accounts.launch.value,
          ),
        ),
      ],
    });
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('launch', accounts.launch),
      getAccountMeta('launchAuthority', accounts.launchAuthority),
      getAccountMeta('baseVault', accounts.baseVault),
      getAccountMeta('quoteVault', accounts.quoteVault),
      getAccountMeta('userBaseAccount', accounts.userBaseAccount),
      getAccountMeta('userQuoteAccount', accounts.userQuoteAccount),
      getAccountMeta('baseMint', accounts.baseMint),
      getAccountMeta('quoteMint', accounts.quoteMint),
      getAccountMeta('user', accounts.user),
      getAccountMeta('hookProgram', accounts.hookProgram),
      getAccountMeta('launchFeeState', accounts.launchFeeState),
      getAccountMeta('baseTokenProgram', accounts.baseTokenProgram),
      getAccountMeta('quoteTokenProgram', accounts.quoteTokenProgram),
    ],
    data: getCurveSwapExactInInstructionDataEncoder().encode(
      args as CurveSwapExactInInstructionDataArgs,
    ),
    programAddress,
  } as CurveSwapExactInInstruction<
    TProgramAddress,
    TAccountLaunch,
    TAccountLaunchAuthority,
    TAccountBaseVault,
    TAccountQuoteVault,
    TAccountUserBaseAccount,
    TAccountUserQuoteAccount,
    TAccountBaseMint,
    TAccountQuoteMint,
    TAccountUser,
    TAccountHookProgram,
    TAccountLaunchFeeState,
    TAccountBaseTokenProgram,
    TAccountQuoteTokenProgram
  >);
}

export type CurveSwapExactInInput<
  TAccountLaunch extends string = string,
  TAccountLaunchAuthority extends string = string,
  TAccountBaseVault extends string = string,
  TAccountQuoteVault extends string = string,
  TAccountUserBaseAccount extends string = string,
  TAccountUserQuoteAccount extends string = string,
  TAccountBaseMint extends string = string,
  TAccountQuoteMint extends string = string,
  TAccountUser extends string = string,
  TAccountHookProgram extends string = string,
  TAccountLaunchFeeState extends string = string,
  TAccountBaseTokenProgram extends string = string,
  TAccountQuoteTokenProgram extends string = string,
> = {
  launch: Address<TAccountLaunch>;
  launchAuthority: Address<TAccountLaunchAuthority>;
  baseVault: Address<TAccountBaseVault>;
  quoteVault: Address<TAccountQuoteVault>;
  userBaseAccount: Address<TAccountUserBaseAccount>;
  userQuoteAccount: Address<TAccountUserQuoteAccount>;
  baseMint: Address<TAccountBaseMint>;
  quoteMint: Address<TAccountQuoteMint>;
  user: TransactionSigner<TAccountUser>;
  /** Optional hook program (must match launch.hook_program if set) */
  hookProgram?: Address<TAccountHookProgram>;
  launchFeeState: Address<TAccountLaunchFeeState>;
  baseTokenProgram: Address<TAccountBaseTokenProgram>;
  quoteTokenProgram: Address<TAccountQuoteTokenProgram>;
  amountIn: CurveSwapExactInInstructionDataArgs['amountIn'];
  minAmountOut: CurveSwapExactInInstructionDataArgs['minAmountOut'];
  tradeDirection: CurveSwapExactInInstructionDataArgs['tradeDirection'];
};

export function getCurveSwapExactInInstruction<
  TAccountLaunch extends string,
  TAccountLaunchAuthority extends string,
  TAccountBaseVault extends string,
  TAccountQuoteVault extends string,
  TAccountUserBaseAccount extends string,
  TAccountUserQuoteAccount extends string,
  TAccountBaseMint extends string,
  TAccountQuoteMint extends string,
  TAccountUser extends string,
  TAccountHookProgram extends string,
  TAccountLaunchFeeState extends string,
  TAccountBaseTokenProgram extends string,
  TAccountQuoteTokenProgram extends string,
  TProgramAddress extends Address = typeof INITIALIZER_PROGRAM_ADDRESS,
>(
  input: CurveSwapExactInInput<
    TAccountLaunch,
    TAccountLaunchAuthority,
    TAccountBaseVault,
    TAccountQuoteVault,
    TAccountUserBaseAccount,
    TAccountUserQuoteAccount,
    TAccountBaseMint,
    TAccountQuoteMint,
    TAccountUser,
    TAccountHookProgram,
    TAccountLaunchFeeState,
    TAccountBaseTokenProgram,
    TAccountQuoteTokenProgram
  >,
  config?: { programAddress?: TProgramAddress },
): CurveSwapExactInInstruction<
  TProgramAddress,
  TAccountLaunch,
  TAccountLaunchAuthority,
  TAccountBaseVault,
  TAccountQuoteVault,
  TAccountUserBaseAccount,
  TAccountUserQuoteAccount,
  TAccountBaseMint,
  TAccountQuoteMint,
  TAccountUser,
  TAccountHookProgram,
  TAccountLaunchFeeState,
  TAccountBaseTokenProgram,
  TAccountQuoteTokenProgram
> {
  // Program address.
  const programAddress = config?.programAddress ?? INITIALIZER_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    launch: { value: input.launch ?? null, isWritable: true },
    launchAuthority: {
      value: input.launchAuthority ?? null,
      isWritable: false,
    },
    baseVault: { value: input.baseVault ?? null, isWritable: true },
    quoteVault: { value: input.quoteVault ?? null, isWritable: true },
    userBaseAccount: { value: input.userBaseAccount ?? null, isWritable: true },
    userQuoteAccount: {
      value: input.userQuoteAccount ?? null,
      isWritable: true,
    },
    baseMint: { value: input.baseMint ?? null, isWritable: false },
    quoteMint: { value: input.quoteMint ?? null, isWritable: false },
    user: { value: input.user ?? null, isWritable: false },
    hookProgram: { value: input.hookProgram ?? null, isWritable: false },
    launchFeeState: { value: input.launchFeeState ?? null, isWritable: true },
    baseTokenProgram: {
      value: input.baseTokenProgram ?? null,
      isWritable: false,
    },
    quoteTokenProgram: {
      value: input.quoteTokenProgram ?? null,
      isWritable: false,
    },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('launch', accounts.launch),
      getAccountMeta('launchAuthority', accounts.launchAuthority),
      getAccountMeta('baseVault', accounts.baseVault),
      getAccountMeta('quoteVault', accounts.quoteVault),
      getAccountMeta('userBaseAccount', accounts.userBaseAccount),
      getAccountMeta('userQuoteAccount', accounts.userQuoteAccount),
      getAccountMeta('baseMint', accounts.baseMint),
      getAccountMeta('quoteMint', accounts.quoteMint),
      getAccountMeta('user', accounts.user),
      getAccountMeta('hookProgram', accounts.hookProgram),
      getAccountMeta('launchFeeState', accounts.launchFeeState),
      getAccountMeta('baseTokenProgram', accounts.baseTokenProgram),
      getAccountMeta('quoteTokenProgram', accounts.quoteTokenProgram),
    ],
    data: getCurveSwapExactInInstructionDataEncoder().encode(
      args as CurveSwapExactInInstructionDataArgs,
    ),
    programAddress,
  } as CurveSwapExactInInstruction<
    TProgramAddress,
    TAccountLaunch,
    TAccountLaunchAuthority,
    TAccountBaseVault,
    TAccountQuoteVault,
    TAccountUserBaseAccount,
    TAccountUserQuoteAccount,
    TAccountBaseMint,
    TAccountQuoteMint,
    TAccountUser,
    TAccountHookProgram,
    TAccountLaunchFeeState,
    TAccountBaseTokenProgram,
    TAccountQuoteTokenProgram
  >);
}

export type ParsedCurveSwapExactInInstruction<
  TProgram extends string = typeof INITIALIZER_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    launch: TAccountMetas[0];
    launchAuthority: TAccountMetas[1];
    baseVault: TAccountMetas[2];
    quoteVault: TAccountMetas[3];
    userBaseAccount: TAccountMetas[4];
    userQuoteAccount: TAccountMetas[5];
    baseMint: TAccountMetas[6];
    quoteMint: TAccountMetas[7];
    user: TAccountMetas[8];
    /** Optional hook program (must match launch.hook_program if set) */
    hookProgram?: TAccountMetas[9] | undefined;
    launchFeeState: TAccountMetas[10];
    baseTokenProgram: TAccountMetas[11];
    quoteTokenProgram: TAccountMetas[12];
  };
  data: CurveSwapExactInInstructionData;
};

export function parseCurveSwapExactInInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedCurveSwapExactInInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 13) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 13,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  const getNextOptionalAccount = () => {
    const accountMeta = getNextAccount();
    return accountMeta.address === INITIALIZER_PROGRAM_ADDRESS
      ? undefined
      : accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      launch: getNextAccount(),
      launchAuthority: getNextAccount(),
      baseVault: getNextAccount(),
      quoteVault: getNextAccount(),
      userBaseAccount: getNextAccount(),
      userQuoteAccount: getNextAccount(),
      baseMint: getNextAccount(),
      quoteMint: getNextAccount(),
      user: getNextAccount(),
      hookProgram: getNextOptionalAccount(),
      launchFeeState: getNextAccount(),
      baseTokenProgram: getNextAccount(),
      quoteTokenProgram: getNextAccount(),
    },
    data: getCurveSwapExactInInstructionDataDecoder().decode(instruction.data),
  };
}
